// Fullscreen image lightbox — lazy DOM, click-to-zoom, pinch-to-zoom

let overlay, containerEl, imgEl;
let isOpen = false;
let isZoomed = false;
let triggerEl = null;

const ptrs = new Map();
let pinchDist0 = 0;
let dimW0 = 0;
let wasPinch = false;

function ptrDist(a, b) {
  return Math.hypot(a.clientX - b.clientX, a.clientY - b.clientY);
}

function ensureDOM() {
  if (overlay) return;

  overlay = document.createElement('div');
  overlay.className = 'image-lightbox-overlay';
  overlay.setAttribute('role', 'dialog');
  overlay.setAttribute('aria-modal', 'true');
  overlay.innerHTML =
    '<button class="image-lightbox-close" aria-label="Close">\u00D7</button>' +
    '<div class="image-lightbox-container">' +
      '<div class="image-lightbox-wrapper">' +
        '<img class="image-lightbox-img" draggable="false" />' +
      '</div>' +
    '</div>';

  containerEl = overlay.querySelector('.image-lightbox-container');
  imgEl = overlay.querySelector('.image-lightbox-img');
  document.body.appendChild(overlay);

  overlay.querySelector('.image-lightbox-close').addEventListener('click', close);
  overlay.addEventListener('click', (e) => {
    if (e.target === overlay) close();
  });
  document.addEventListener('keydown', (e) => {
    if (e.key === 'Escape' && isOpen) close();
  });

  containerEl.addEventListener('click', (e) => {
    if (wasPinch) return;
    if (e.target === imgEl) toggleZoom(e);
    else if (!isZoomed) close();
  });

  containerEl.addEventListener('pointerdown', onDown);
  containerEl.addEventListener('pointermove', onMove, { passive: false });
  containerEl.addEventListener('pointerup', onUp);
  containerEl.addEventListener('pointercancel', onUp);
}

function toggleZoom(e) {
  if (!isZoomed) {
    const w = Math.max(imgEl.naturalWidth, containerEl.clientWidth * 2);
    const h = Math.round(w * (imgEl.naturalHeight / imgEl.naturalWidth));
    zoomTo(w, h, e);
  } else {
    resetZoom();
  }
}

function zoomTo(w, h, e) {
  const rect = imgEl.getBoundingClientRect();
  const rx = e ? (e.clientX - rect.left) / rect.width : 0.5;
  const ry = e ? (e.clientY - rect.top) / rect.height : 0.5;

  imgEl.classList.add('zoomed');
  imgEl.style.width = w + 'px';
  imgEl.style.height = h + 'px';
  isZoomed = true;

  requestAnimationFrame(() => {
    containerEl.scrollLeft = w * rx - containerEl.clientWidth / 2;
    containerEl.scrollTop = h * ry - containerEl.clientHeight / 2;
  });
}

function resetZoom() {
  imgEl.classList.remove('zoomed');
  imgEl.style.width = '';
  imgEl.style.height = '';
  imgEl.style.transform = '';
  containerEl.scrollLeft = 0;
  containerEl.scrollTop = 0;
  isZoomed = false;
}

// --- Pinch-to-zoom ---

function onDown(e) {
  ptrs.set(e.pointerId, e);
  if (ptrs.size === 2) {
    wasPinch = true;
    const [a, b] = [...ptrs.values()];
    pinchDist0 = ptrDist(a, b);
    if (!isZoomed) {
      const rect = imgEl.getBoundingClientRect();
      imgEl.classList.add('zoomed');
      imgEl.style.width = rect.width + 'px';
      imgEl.style.height = rect.height + 'px';
      isZoomed = true;
    }
    dimW0 = parseFloat(imgEl.style.width);
  }
}

function onMove(e) {
  if (!ptrs.has(e.pointerId)) return;
  ptrs.set(e.pointerId, e);
  if (ptrs.size >= 2 && wasPinch) {
    e.preventDefault();
    const [a, b] = [...ptrs.values()];
    const scale = ptrDist(a, b) / pinchDist0;
    imgEl.style.transform = `scale(${scale})`;
  }
}

function onUp(e) {
  ptrs.delete(e.pointerId);
  if (!wasPinch || ptrs.size >= 2) return;

  const m = imgEl.style.transform.match(/scale\(([^)]+)\)/);
  const s = m ? parseFloat(m[1]) : 1;
  imgEl.style.transform = '';

  const newW = dimW0 * s;
  const ratio = imgEl.naturalHeight / imgEl.naturalWidth;
  const newH = newW * ratio;

  if (newW < containerEl.clientWidth * 0.85 && newH < containerEl.clientHeight * 0.85) {
    resetZoom();
  } else {
    const oldW = parseFloat(imgEl.style.width) || dimW0;
    const oldH = parseFloat(imgEl.style.height) || dimW0 * ratio;
    const cx = (containerEl.scrollLeft + containerEl.clientWidth / 2) / oldW;
    const cy = (containerEl.scrollTop + containerEl.clientHeight / 2) / oldH;

    imgEl.style.width = newW + 'px';
    imgEl.style.height = newH + 'px';

    requestAnimationFrame(() => {
      containerEl.scrollLeft = cx * newW - containerEl.clientWidth / 2;
      containerEl.scrollTop = cy * newH - containerEl.clientHeight / 2;
    });
  }
  setTimeout(() => { wasPinch = false; }, 200);
}

// --- Public API ---

export function open(src, alt) {
  ensureDOM();
  triggerEl = document.activeElement;
  imgEl.src = src;
  imgEl.alt = alt || '';
  resetZoom();
  overlay.offsetHeight; // force reflow so the transition plays
  overlay.classList.add('active');
  document.body.style.overflow = 'hidden';
  isOpen = true;
  overlay.querySelector('.image-lightbox-close').focus();
}

export function close() {
  if (!overlay || !isOpen) return;
  overlay.classList.remove('active');
  document.body.style.overflow = '';
  resetZoom();
  isOpen = false;
  ptrs.clear();
  wasPinch = false;
  if (triggerEl) {
    triggerEl.focus();
    triggerEl = null;
  }
}
